import { FunctionComponent } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { useTranslation } from 'react-i18next'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './confirmDeactiveNotificationAccountDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { DEACTIVATE_ACCOUNT_MAIL_NOTIFICATION } from '@/modules/API/software/emailHandler'
import { EmailUser } from '@/types/EmailHandlerType'
import EmailData from '../../emailData'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ConfirmDeactiveAccountProps {
    open: boolean
    handleClose: () => void
    email: string
    setEmail: (email: string) => void
    emailDeactivationData?: EmailUser
    setEmailDeactivationData: (data: EmailUser | undefined) => void
}

const ConfirmDeactiveNotificationAccountDialog: FunctionComponent<ConfirmDeactiveAccountProps> = ({
    open,
    handleClose,
    email,
    setEmail,
    emailDeactivationData,
    setEmailDeactivationData
}: ConfirmDeactiveAccountProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [deactivateAccountNotification] = useMutation(DEACTIVATE_ACCOUNT_MAIL_NOTIFICATION, {
        onCompleted: () => {
            showNotificationSuccess(t('deactiveAccountNotificationSuccess'))

            setEmail('')
            setEmailDeactivationData(undefined)

            handleClose()
        },
        onError: (error) => {
            if (error.message.split('Message: ')[0] === 'Notification already disabled') {
                showNotificationError(t('notificationAlreadyDeactivated'))
            } else {
                showNotificationError(t('deactiveAccountNotificationError'))
            }
            handleClose()
        }
    })

    function handleDeactivateAccountNotification(push: boolean): void {
        deactivateAccountNotification({
            variables: {
                email: email,
                push: push
            }
        })
    }

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('deactivateAccountNotification')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('deactivateAccountNotificationMessage')}</p>
                <div className={styles.Text}>
                    <span className={styles.TextEmail}> {email} </span>
                    <EmailData data={emailDeactivationData} loading={false} />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('pushNotification')}
                        customIcon={<Check />}
                        onClick={() => handleDeactivateAccountNotification(true)}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('emailNotification')}
                        customIcon={<Check />}
                        onClick={() => handleDeactivateAccountNotification(false)}
                        className={styles.Button}
                    />
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ConfirmDeactiveNotificationAccountDialog
